import { useCallback, useEffect, useRef, useState } from "react";
import type { CameraPermission } from "../camera/types";

const constraints = (deviceId?: string): MediaStreamConstraints => ({
  audio: false,
  video: {
    width: { ideal: 1920 },
    height: { ideal: 1080 },
    facingMode: { ideal: "environment" },
    ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
  },
});

function permissionFromError(error: unknown): CameraPermission {
  if (!(error instanceof DOMException)) return "unreadable";
  if (["NotAllowedError", "SecurityError"].includes(error.name)) return "denied";
  if (["NotFoundError", "DevicesNotFoundError"].includes(error.name)) return "missing";
  return "unreadable";
}

export function useCamera() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const activeTrackRef = useRef<MediaStreamTrack | null>(null);
  const startGenerationRef = useRef(0);
  const selectedDeviceIdRef = useRef("");
  const hasMediaDevices =
    typeof navigator !== "undefined" && Boolean(navigator.mediaDevices);
  const [permission, setPermission] = useState<CameraPermission>(
    hasMediaDevices ? "idle" : "missing",
  );
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [resolution, setResolution] = useState<{ width: number; height: number } | null>(
    null,
  );
  const [error, setError] = useState<string | null>(
    hasMediaDevices ? null : "This browser does not expose the required camera APIs.",
  );

  const stopCurrentStream = useCallback(() => {
    const stream = streamRef.current;
    streamRef.current = null;
    activeTrackRef.current = null;
    stream?.getTracks().forEach((track) => track.stop());
    setResolution(null);
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const stop = useCallback(() => {
    startGenerationRef.current += 1;
    stopCurrentStream();
  }, [stopCurrentStream]);

  const refreshDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) {
      setDevices([]);
      return [] as MediaDeviceInfo[];
    }
    try {
      const mediaDevices = await navigator.mediaDevices.enumerateDevices();
      const cameras = mediaDevices.filter(
        (device) => device.kind === "videoinput" && device.deviceId,
      );
      setDevices(cameras);
      return cameras;
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "The camera list could not be refreshed.",
      );
      return [] as MediaDeviceInfo[];
    }
  }, []);

  const start = useCallback(
    async (deviceId?: string) => {
      const generation = startGenerationRef.current + 1;
      startGenerationRef.current = generation;
      setPermission("requesting");
      setError(null);
      stopCurrentStream();
      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints(deviceId));
        if (generation !== startGenerationRef.current) {
          stream.getTracks().forEach((item) => item.stop());
          return;
        }
        const [track] = stream.getVideoTracks();
        if (!track) throw new DOMException("No camera track", "NotFoundError");
        track.addEventListener("ended", () => {
          if (activeTrackRef.current === track) {
            streamRef.current = null;
            activeTrackRef.current = null;
            setResolution(null);
            setPermission("disconnected");
            setError("The selected camera disconnected. Reconnect it or choose another camera.");
          }
        });
        streamRef.current = stream;
        activeTrackRef.current = track;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        if (generation !== startGenerationRef.current) {
          stream.getTracks().forEach((item) => item.stop());
          return;
        }
        const settings = track.getSettings();
        const width = settings.width ?? videoRef.current?.videoWidth ?? 0;
        const height = settings.height ?? videoRef.current?.videoHeight ?? 0;
        setResolution(width > 0 && height > 0 ? { width, height } : null);
        setPermission("granted");
        const selected = settings.deviceId ?? deviceId ?? "";
        selectedDeviceIdRef.current = selected;
        setSelectedDeviceId(selected);
        await refreshDevices();
      } catch (caught) {
        if (generation !== startGenerationRef.current) return;
        stopCurrentStream();
        setPermission(permissionFromError(caught));
        setError(
          caught instanceof DOMException || caught instanceof Error
            ? caught.message
            : "Camera could not be opened",
        );
      }
    },
    [refreshDevices, stopCurrentStream],
  );

  useEffect(() => {
    if (!navigator.mediaDevices) return;
    const changed = async () => {
      const selected = selectedDeviceIdRef.current;
      const cameras = await refreshDevices();
      const selectedStillExists = cameras.some(
        (device) => device.deviceId === selected,
      );
      if (
        selected &&
        selectedDeviceIdRef.current === selected &&
        !selectedStillExists
      ) {
        setPermission("disconnected");
      }
    };
    void changed();
    const deviceChangeListener = () => void changed();
    navigator.mediaDevices.addEventListener("devicechange", deviceChangeListener);
    return () => {
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        deviceChangeListener,
      );
      stop();
    };
  }, [refreshDevices, stop]);

  const select = useCallback(
    async (deviceId: string) => {
      selectedDeviceIdRef.current = deviceId;
      setSelectedDeviceId(deviceId);
      await start(deviceId || undefined);
    },
    [start],
  );

  return {
    videoRef,
    streamRef,
    permission,
    devices,
    selectedDeviceId,
    resolution,
    lowResolution:
      resolution !== null &&
      (resolution.width < 1280 || resolution.height < 720),
    error,
    start,
    stop,
    refreshDevices,
    select,
  };
}
